import React, { FunctionComponent } from 'react';
import Media from 'react-media';
import styled from 'styled-components';

import THEME from 'theme';
import ICONS from 'components/Icons';
import { customerCareNumber } from 'scenes/common/helpers/contacts';
import { SCREEN } from 'scenes/common/types/screen';
import { ScreenReaderContainer } from 'components';
import { toPercentage } from 'scenes/common/helpers/percentage';
import { ProgressBarFlow } from 'scenes/common/types';

const Wrapper = styled.div`
  display: flex;
  padding: 1rem 1rem 0.7rem 0;
  width: 100%;
  justify-content: space-between;

  @media (min-width: ${SCREEN.SMALL}px) {
    margin-top: 1rem;
    padding: 1rem 1rem 0.7rem 1rem;
  }

  @media (max-width: ${SCREEN.LARGE}px) {
    padding-left: 16px;
  }
`;

const ColesImage = styled.img`
  width: 160px;
  height: 2rem;

  @media (min-width: ${SCREEN.SMALL}px) {
    width: 247px;
  }
`;

const CallSymbolLink = styled.a`
  z-index: 99;
`;

const CallSymbolImage = styled.img`
  height: 2rem;
  pointer-events: none;
`;

const Separator = styled.div`
  height: 3px;
  background-color: ${THEME.BADGE.BACKGROUND.PRIMARY};

  @media (min-width: ${SCREEN.SMALL}px) and (max-width: ${SCREEN.LARGE - 1}px) {
    margin-right: 1rem;
    margin-left: 1rem;
  }
`;

const HeaderBottom = styled.div<{ hasProgressFlowBar: boolean }>`
  margin-top: ${({ hasProgressFlowBar }) =>
    hasProgressFlowBar ? 'none' : '-55px'};
  position: relative;
  height: ${({ hasProgressFlowBar }) => (hasProgressFlowBar ? '0' : '80px')};
  display: flex;
  align-items: flex-end;

  @media (min-width: ${SCREEN.SMALL}px) {
    margin-top: -75px;
    height: ${({ hasProgressFlowBar }) =>
      hasProgressFlowBar ? '80px' : '125px'};
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;

const ProgressBackground = styled.div`
  height: 6px;
  width: 100%;
  min-width: 100%;
  background-color: #e8e5da;

  @media (min-width: ${SCREEN.SMALL}px) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const ProgressBarIconWrapper = styled.div<{ progress: number }>`
  position: absolute;
  display: flex;
  justify-content: flex-end;
  border-bottom: 6px solid #404040;
  height: 140px;
  left: 0;
  transition-property: width;
  transition-duration: 0.7s;
  max-width: 100%;

  min-width: 60px;
  width: ${({ progress = 0 }) =>
    progress >= 96 ? `100%` : `calc(${progress}% + 60px)`};
  @media (min-width: ${SCREEN.SMALL}px) {
    min-width: 80px;
    width: ${({ progress = 0 }) =>
      progress >= 96 ? `100%` : `calc(${progress}% + 80px)`};
  }
`;

export const ProgressFlowBarWrapper = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${THEME.BACKGROUND.CARD};
  padding: 1rem;

  @media (min-width: ${SCREEN.SMALL}px) {
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;

const CallSymbol: FunctionComponent<{
  onClickCallUs: () => void;
}> = ({ onClickCallUs }) => (
  <CallSymbolLink
    onClick={onClickCallUs}
    href={`tel:${customerCareNumber()}`}
    title="Call Coles contact centre"
  >
    <CallSymbolImage
      src={ICONS.CORE.PHONE}
      tabIndex={-1}
      alt="Call Coles contact centre"
    />
  </CallSymbolLink>
);

export const HeaderTop: FunctionComponent<{
  progress?: number;
  showProgressBar?: boolean;
  ProgressBarIcon?: FunctionComponent;
  ProgressFlowBar?: FunctionComponent;
  onClickCallUs: () => void;
  currentProgressBarFlow?: ProgressBarFlow;
  MotorProgressBar?: JSX.Element;
  motorExperimentalStyles?: boolean;
}> = ({
  showProgressBar,
  progress = 0,
  ProgressBarIcon,
  ProgressFlowBar,
  onClickCallUs,
  currentProgressBarFlow,
  MotorProgressBar,
  motorExperimentalStyles,
}) => (
  <Media query={{ maxWidth: SCREEN.SMALL - 1 }}>
    {(matches) => (
      <>
        <Wrapper>
          <ColesImage
            src={ICONS.LOGO.COLES_INSURANCE}
            tabIndex={-1}
            alt="Coles Insurance Logo"
          />
          {matches && <CallSymbol onClickCallUs={onClickCallUs} />}
        </Wrapper>
        {motorExperimentalStyles && <Separator />}
        {MotorProgressBar && (
          <>
            <ScreenReaderContainer>
              {toPercentage(progress)} complete of {currentProgressBarFlow}
            </ScreenReaderContainer>
            {MotorProgressBar}
          </>
        )}
        {showProgressBar && ProgressFlowBar && (
          <ProgressFlowBarWrapper>
            <ScreenReaderContainer>
              {toPercentage(progress)} complete of {currentProgressBarFlow}
            </ScreenReaderContainer>
            <ProgressFlowBar />
          </ProgressFlowBarWrapper>
        )}
        {showProgressBar && (
          <HeaderBottom hasProgressFlowBar={Boolean(ProgressFlowBar)}>
            <ProgressBarIconWrapper progress={progress}>
              {ProgressBarIcon && <ProgressBarIcon />}
            </ProgressBarIconWrapper>
            <ProgressBackground />
          </HeaderBottom>
        )}
      </>
    )}
  </Media>
);
